import { useCallback } from 'react'
import { isEmpty } from 'ramda'

import { useReportResult } from 'app/dataProviders/reports/hooks'
import { Loading } from 'app/common/templates-next/page-template'
import { ReportSection, Stub, ReportDetailsRoot } from 'app/common/templates-next/report-template'
import { ReportStatusIndicator } from 'app/components/common/reports/v3/ReportStatusIndicator'
import { OrderByClassesDetailsGroup } from 'app/dataTypes/reports/data/common/ordersByClasses'
import { ReportType } from 'app/dataTypes/reports'

import Summary from './summary'
import { HeaderBar } from './HeaderBar'
import { UnperformedWarning } from './unperformed'
import { GroupByClasses } from './group-by-classes/group-by-classes'
import { OrgsGrouppedByFunds } from './group-by-funds/orgs-groupped-by-funds'
import { OrgsGrouppedByCustomersAndFunds } from './group-by-customers-and-funds/orgs-groupped-by-customers-and-funds'
import { GrouppedByComplexesAndAllowances } from './group-by-complexes-and-allowances/groupped-by-complexes-and-allowances'

type Props = {
  reportID: null | string
  onReportId?: (reportID: string | null) => void
}

export const Result = ({ reportID, onReportId }: Props) => {
  const resetReport = useCallback(() => onReportId?.(null), [onReportId])

  const { requestResult, data, loading } = useReportResult(reportID, ReportType.OrdersByClasses)

  const hasDetails =
    data &&
    !!(
      (data.detailsGroup === OrderByClassesDetailsGroup.ByClasses && data.orgs) ||
      data.detailsGroup === OrderByClassesDetailsGroup.ByFunds ||
      data.detailsGroup === OrderByClassesDetailsGroup.ByCustomersAndFunds ||
      data.detailsGroup === OrderByClassesDetailsGroup.ByComplexesAndAllowances
    )
  const reportEmpty = !reportID
  const reportPrepare = !(reportID && data)

  const header = reportEmpty ? '' : 'Отчет «Учет количества питающихся»'

  return (
    <ReportSection
      header={header}
      headerBar={<HeaderBar reportID={reportID} reportReady={!reportPrepare && !reportEmpty} />}
      fullscreenEnabled={!reportPrepare && !reportEmpty && !!hasDetails}
      skipPadding
    >
      {reportID && (
        <ReportStatusIndicator reportID={reportID} onFail={resetReport} onSuccess={requestResult} />
      )}

      {reportPrepare && <Stub />}

      {loading && data && !data.detailsGroup && <Loading />}

      {!reportPrepare && data && data.detailsGroup === OrderByClassesDetailsGroup.ByClasses && (
        <Summary
          summary={data.summary}
          pending={loading}
          hasUnperformed={!isEmpty(data.unperformed)}
        />
      )}

      {data && data.detailsGroup && <UnperformedWarning unperformed={data.unperformed} />}

      {hasDetails && (
        <ReportDetailsRoot header="Детализация отчета «Учет количества питающихся»">
          {data.detailsGroup === OrderByClassesDetailsGroup.ByClasses && data.orgs && (
            <GroupByClasses data={data.orgs} />
          )}

          {data.detailsGroup === OrderByClassesDetailsGroup.ByFunds && data.orgs && (
            <OrgsGrouppedByFunds
              allowanceIDs={data.allowanceIDs}
              totals={data.totals}
              data={data.orgs}
              pending={loading}
            />
          )}

          {data.detailsGroup === OrderByClassesDetailsGroup.ByCustomersAndFunds && data.orgs && (
            <OrgsGrouppedByCustomersAndFunds
              allowances={data.allowances}
              totals={data.totals}
              data={data.orgs}
              pending={loading}
            />
          )}

          {data.detailsGroup === OrderByClassesDetailsGroup.ByComplexesAndAllowances && (
            <GrouppedByComplexesAndAllowances
              dateKeys={data.dateKeys}
              dateKeysFormat={data.dateKeysFormat}
              data={data.data}
              pending={loading}
            />
          )}
        </ReportDetailsRoot>
      )}
    </ReportSection>
  )
}
